import { Link } from "react-router-dom";
import { Plan } from "../../models/SubscriptionModels";
import "./PricingCards.css";

interface PricingCardsProps {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

/**
 * Honest, capability-level highlights by plan code. Only describes what the
 * product actually does — never invent limits the API doesn't return.
 */
const PLAN_HIGHLIGHTS: Record<string, string[]> = {
  starter: [
    "One organization with core branches",
    "Clients, services & appointments",
    "Public online booking page",
    "Email support",
  ],
  growth: [
    "Everything in Starter",
    "More branches and staff roles",
    "Follow-ups & client messaging",
    "Website content tools & inquiries",
    "Priority support",
  ],
};

const formatPrice = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
};

const formatPeriod = (billingPeriodDays: number): string => {
  if (billingPeriodDays >= 28 && billingPeriodDays <= 31) return "per month";
  if (billingPeriodDays === 7) return "per week";
  if (billingPeriodDays >= 365 && billingPeriodDays <= 366) return "per year";
  return `every ${billingPeriodDays} days`;
};

const PricingCards = ({ plans, loading, error, onRetry }: PricingCardsProps) => {
  if (loading) {
    return (
      <div className="pricing-cards" aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className="plan-card plan-card--skeleton">
            <span className="plan-card__sk plan-card__sk--title" />
            <span className="plan-card__sk plan-card__sk--line" />
            <span className="plan-card__sk plan-card__sk--price" />
            <span className="plan-card__sk plan-card__sk--line" />
            <span className="plan-card__sk plan-card__sk--line plan-card__sk--short" />
            <span className="plan-card__sk plan-card__sk--button" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="pricing-cards__state" role="alert">
        <p className="pricing-cards__state-title">Plans didn't load</p>
        <p className="pricing-cards__state-text">{error}</p>
        <button
          type="button"
          className="pricing-cards__retry"
          onClick={onRetry}
        >
          Try again
        </button>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="pricing-cards__state">
        <p className="pricing-cards__state-title">Plans coming soon</p>
        <p className="pricing-cards__state-text">
          We're finalizing plan details. Call{" "}
          <a href="tel:01916707697">01916707697</a> or email{" "}
          <a href="mailto:zonayed@carelyit.com">zonayed@carelyit.com</a> and
          we'll walk you through current options.
        </p>
      </div>
    );
  }

  return (
    <div className="pricing-cards">
      {plans.map((plan) => {
        const highlights = PLAN_HIGHLIGHTS[plan.code.toLowerCase()];
        return (
          <article key={plan.id} className="plan-card">
            <h2 className="plan-card__name">{plan.name}</h2>
            {plan.description && (
              <p className="plan-card__description">{plan.description}</p>
            )}
            <p className="plan-card__price">
              <span className="plan-card__amount">
                {formatPrice(plan.priceMonthly, plan.currency)}
              </span>
              <span className="plan-card__period">
                {formatPeriod(plan.billingPeriodDays)}
              </span>
            </p>
            {highlights && (
              <ul className="plan-card__highlights">
                {highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            )}
            <div className="plan-card__actions">
              <a className="plan-card__cta" href="tel:01916707697">
                Call to subscribe
              </a>
              <span className="plan-card__login-hint">
                Already a customer? <Link to="/login">Login</Link>
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default PricingCards;
